import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LeadPicker } from "@/components/crm/appointments/LeadPicker";

export const APPT_STATUSES = ["Agendada", "Confirmada", "Realizada", "Cancelada", "No asistió"];
const tz = () => new Date().getTimezoneOffset();

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;

export const AppointmentDialog = ({ open, onOpenChange, lead, appointment, defaults = {}, requestId, setStage, onSaved, onCancel }) => {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({});
  const [pickedLead, setPickedLead] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = Boolean(appointment);

  useEffect(() => {
    if (!open) return;
    api.get("/public/services").then((r) => setServices(r.data)).catch(() => {});
    setError("");
    setPickedLead(lead || null);
    setForm(appointment
      ? { service_id: appointment.service_id || "", date: appointment.date, time: appointment.time, duration_min: appointment.duration_min, notes: appointment.notes || "", status: appointment.status }
      : { service_id: lead?.service_id || "", date: defaults.date || "", time: defaults.time || "", duration_min: "", notes: "" });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const close = (v) => { if (!v && !saving) { onCancel?.(); onOpenChange(false); } };

  const save = async () => {
    if (!form.date || !form.time) { setError("Ingresa una fecha y hora válidas"); return; }
    const target = pickedLead || lead;
    if (!editing && !target) { setError("Selecciona un lead"); return; }
    setSaving(true);
    setError("");
    try {
      const payload = { service_id: form.service_id || null, date: form.date, time: form.time, notes: form.notes, tz_offset: tz(),
        ...(form.duration_min ? { duration_min: Number(form.duration_min) } : {}) };
      const { data } = editing
        ? await api.patch(`/appointments/${appointment.id}`, { ...payload, status: form.status })
        : await api.post("/appointments", { ...payload, lead_id: target.id, request_id: requestId, set_stage: Boolean(setStage) });
      toast.success(editing ? "Cita actualizada" : "Cita agendada");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) { setError(errorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle>{editing ? "Editar cita" : "Agendar cita"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {editing || lead ? (
            <p className="text-sm text-slate-600" data-testid="appointment-dialog-lead">Paciente: <b className="text-[#0B2545]">{appointment?.lead_name || lead?.full_name}</b></p>
          ) : <Field label="Paciente (lead)"><LeadPicker value={pickedLead} onChange={setPickedLead} /></Field>}
          <Field label="Servicio">
            <Select value={form.service_id || undefined} onValueChange={set("service_id")}>
              <SelectTrigger className="rounded-xl" data-testid="appointment-service-select"><SelectValue placeholder="Selecciona servicio" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Fecha"><Input type="date" value={form.date || ""} onChange={set("date")} className="rounded-xl" data-testid="appointment-date-input" /></Field>
            <Field label="Hora"><Input type="time" value={form.time || ""} onChange={set("time")} className="rounded-xl" data-testid="appointment-time-input" /></Field>
            <Field label="Duración (min)"><Input type="number" min="5" placeholder="Servicio" value={form.duration_min || ""} onChange={set("duration_min")} className="rounded-xl" data-testid="appointment-duration-input" /></Field>
          </div>
          {editing && (
            <Field label="Estado">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-xl" data-testid="appointment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{APPT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Observaciones"><Textarea rows={2} value={form.notes || ""} onChange={set("notes")} className="rounded-xl" data-testid="appointment-notes-input" /></Field>
          {error && <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="appointment-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => close(false)} data-testid="appointment-cancel-button">Cancelar</Button>
          <Button className="rounded-xl bg-[#1E6FD9]" onClick={save} disabled={saving} data-testid="appointment-save-button">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
